import { Component, TemplateFunction } from '@ribajs/core'
import { getUID } from '@ribajs/utils'

interface Tab {
  active: boolean
  content: {
    id: string
    html: string
    class: string
    name: string
  }
  label: {
    id: string
    html: string
    class: string
    name: string
  }
  index: number
}

interface Scope {
  show: SbTabsComponent['show']
  showByLabel: SbTabsComponent['showByLabel']
  showByName: SbTabsComponent['showByName']
  tabs: Tab[]
  handleKeyDown: SbTabsComponent['handleKeyDown']
}

/** A simple tabs component */
export class SbTabsComponent extends Component {
  public static tagName = 'sb-tabs'

  public scope: Scope = {
    show: this.show,
    showByLabel: this.showByLabel,
    showByName: this.showByName,
    tabs: [],
    handleKeyDown: this.handleKeyDown,
  }

  static get observedAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbTabsComponent.observedAttributes)
  }

  /** Handle keyboard navigation for tabs */
  public handleKeyDown(event: KeyboardEvent) {
    const activeElement = document.activeElement
    const tabButtons = Array.from(this.querySelectorAll('[role="tab"]'))

    // Get the current focused tab index
    const currentIndex = tabButtons.indexOf(activeElement as Element)
    if (currentIndex === -1) return

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        // Focus next tab or wrap to first
        const nextIndex =
          currentIndex === tabButtons.length - 1 ? 0 : currentIndex + 1
        ;(tabButtons[nextIndex] as HTMLButtonElement).focus()
        this.show(nextIndex)
        break

      case 'ArrowLeft':
        event.preventDefault()
        // Focus previous tab or wrap to last
        const prevIndex =
          currentIndex === 0 ? tabButtons.length - 1 : currentIndex - 1
        ;(tabButtons[prevIndex] as HTMLButtonElement).focus()
        this.show(prevIndex)
        break

      case 'Home':
        event.preventDefault()
        // Focus first tab
        ;(tabButtons[0] as HTMLButtonElement).focus()
        this.show(0)
        break

      case 'End':
        event.preventDefault()
        // Focus last tab
        const lastIndex = tabButtons.length - 1
        ;(tabButtons[lastIndex] as HTMLButtonElement).focus()
        this.show(lastIndex)
        break
    }
  }

  /** Open / show tab by label */
  public showByLabel(label: string, event?: Event) {
    const index = this.scope.tabs.findIndex(tab => tab.label.html === label)
    if (index < 0) {
      this.error(`Tab with label "${label} not found!"`, this.scope.tabs)
      return
    }
    this.show(index, event)
  }

  /** Open / show tab by name */
  public showByName(name: string, event?: Event) {
    const index = this.scope.tabs.findIndex(tab => tab.label.name === name)
    if (index < 0) {
      this.error(`Tab with name "${name} not found!"`, this.scope.tabs)
      return
    }
    this.show(index, event)
  }

  /** Open / show tab by index */
  public show(index: number, event?: Event) {
    event?.preventDefault()
    this.hideAll(index)
    this.scope.tabs[index].active = true
  }

  /**
   * Hide / close all tabs
   * @param except Except this tab index
   */
  public hideAll(except?: number) {
    for (const tabLabel of this.scope.tabs) {
      if (except !== tabLabel.index) {
        tabLabel.active = false
      }
    }
  }

  /**
   * Called before this custom element is bound and ready
   */
  async beforeBind() {
    await super.beforeBind()
    this.initTabs()
  }

  /**
   * Called after this custom element is bound and ready
   */
  protected async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbTabsComponent.tagName}-ready`)
  }

  protected initTabs() {
    if (!this.id) {
      this.id = getUID(`sb-tabs-`)
    }

    const tabLabelEls = Array.from(this.querySelectorAll('[role="tab"]'))

    const tabContentEls = Array.from(this.querySelectorAll('[role="tabpanel"]'))

    if (tabLabelEls.length !== tabContentEls.length) {
      throw new Error(
        `You must have as many tab labels as tab contents! ${tabLabelEls.length} (labels) !== ${tabContentEls.length} (contents)`,
      )
    }

    // The first one is the riba template
    if (tabLabelEls.length <= 1) {
      throw new Error('No tab definitions found!')
    }

    // skip the first element, this is the riba template
    for (let i = 0; i < tabLabelEls.length - 1; i++) {
      const tabLabelEl = tabLabelEls[i + 1]
      const tabContentEl = tabContentEls[i + 1]

      this.scope.tabs.push({
        active: i === 0 ? true : false, // The first tab is active by default
        index: i,
        label: {
          id: tabLabelEl.id || `${this.id}-label-${i}`,
          html: tabLabelEl.innerHTML.trim(),
          class: tabLabelEl.classList.toString(),
          name: tabLabelEl.getAttribute('name') || '',
        },
        content: {
          id: tabContentEl.id || `${this.id}-content-${i}`,
          html: tabContentEl.innerHTML.trim(),
          class: tabContentEl.classList.toString(),
          name: tabContentEl.getAttribute('name') || '',
        },
      })

      tabLabelEl.remove()
      tabContentEl.remove()
    }
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
